'use client';

import { useSelector } from '@xstate/store/react';
import { useRef } from 'react';

import {
  GTMEcommerceItem,
  GTMItemListName,
  GTMViewItemList,
  sendGTMEventEcommerce
} from '@/fo/api/gtm';
import {
  selectCountProductInCart,
  selectCountProductInWishlist,
  storeProduct,
  cartActions,
  wishlistActions,
  compareActions,
  selectWishlistItemId,
  selectCartItemLoading,
  selectCompareProducts
} from '@/fo/store/product';
import { storeProductVariant } from '@/fo/store/product-variant';
import { UiProductTileBase } from '@/fo/ui/product/tile/base';
import { UiProductTileSearch } from '@/fo/ui/product/tile/search';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventCartModal } from '@/fo/util/event-cart-modal';
import { utilEventProductSubscribeModal } from '@/fo/util/event-product-subscribe-modal';
import { MappedProduct } from '@/fo/util/map-product-data';

export interface FeatureProductTileBaseProps {
  isAuthorized: boolean;
  data: MappedProduct;
  isSelected?: boolean;
  onSelect?: (selected: boolean) => void;
  className?: string;
  type?: 'base' | 'search';
  listName: GTMItemListName;
  categoryName?: string;
  positionIndex?: number;
}

export function FeatureProductTileBase({
  isAuthorized,
  data,
  isSelected = false,
  onSelect,
  className,
  type = 'base',
  listName,
  categoryName,
  positionIndex = 0
}: FeatureProductTileBaseProps) {
  const ref = useRef<HTMLElement>(null);

  const wishlistSku = data.parentSku || data?.sku;

  const cartCount = useSelector(
    storeProduct,
    selectCountProductInCart(data?.sku)
  );
  const cartLoading = useSelector(
    storeProduct,
    selectCartItemLoading(data?.sku)
  );
  const wishlistCount = useSelector(
    storeProduct,
    selectCountProductInWishlist(wishlistSku)
  );
  const wishlistItemId = useSelector(
    storeProduct,
    selectWishlistItemId(wishlistSku)
  );

  const compareItemId = useSelector(
    storeProduct,
    selectCompareProducts(data?.sku)
  );
  const isInWishlist = wishlistCount > 0;
  const isInCompare = compareItemId !== '';
  const isInCart = cartCount > 0;
  const isConfigurable =
    Array.isArray(data?.configurableVariants) &&
    data.configurableVariants.length > 0;
  const isGroupedProduct = Boolean(
    data && 'groupType' in data && data.groupType === 1
  );

  const brand = data.labelsBrand[0];

  const onCartClick = () => {
    if (isInCart) {
      utilEventCartModal.open();
    } else {
      if (isGroupedProduct && data?.items?.length) {
        cartActions.addProductsToCart(
          data?.items.map((item) => ({
            sku: item.sku,
            quantity: 1,
            product: item
          }))
        );
      } else if (isConfigurable) {
        onOptionClick();
      } else {
        cartActions.addProductsToCart([
          {
            sku: data.sku,
            quantity: 1,
            product: data
          }
        ]);
      }
    }
  };

  const onWishlistClick = () => {
    if (isAuthorized) {
      if (isInWishlist && wishlistItemId) {
        wishlistActions.removeWishlistItems([
          { sku: wishlistSku, wishlistItemId }
        ]);
      } else {
        wishlistActions.addProductToWishlist(data.sku, 1, data.parentSku);
      }
    } else {
      utilEventAuthModal.open('wishlist-button');
    }
  };

  const onCompareClick = () => {
    if (isAuthorized) {
      if (isInCompare) {
        compareActions.removeProductFromCompare(data.id);
      } else {
        compareActions.addProductToCompare(data.id, {
          name: data.name,
          imageUrl: data.baseImage?.src ?? ''
        });
      }
    } else {
      utilEventAuthModal.open('compare-button');
    }
  };

  const onOptionClick = () => {
    if (!isConfigurable) return;

    storeProductVariant.send({
      type: 'openModal',
      product: data
    });
  };

  const onSubscribeClick = () => {
    utilEventProductSubscribeModal.open(data.mindboxId);
  };
  const itemGtmData: GTMEcommerceItem = {
    id: data.sku,
    name: data.name,
    price: data.specialPrice ?? data.finalPrice ?? data.basePrice ?? 0,
    brand: brand?.name ?? '',
    category: categoryName || data.brand || '',
    list: listName,
    position: positionIndex + 1
  };

  const onClick = () => {
    sendGTMEventEcommerce({
      event: 'select_item',
      ecommerce: {
        products: [itemGtmData]
      }
    });
  };

  return (
    <>
      <GTMViewItemList
        key={`gtm-${data.sku}`}
        refItem={ref}
        item={itemGtmData}
      />
      {type === 'search' && (
        <UiProductTileSearch
          className={className}
          link={data.link}
          image={data.thumbnail?.src || ''}
          stockStatus={data.stockStatus}
          title={data.name}
          series={data.series}
          price={data.specialPrice ?? data.finalPrice ?? data.basePrice}
          oldPrice={data.finalPrice ? data.basePrice : null}
          currency={data.currency}
          key={data.sku}
        />
      )}

      {type !== 'search' && (
        <UiProductTileBase
          ref={ref}
          key={data.sku}
          stockStatus={data.stockStatus}
          mindboxId={data.mindboxId}
          reviewCount={data.reviewCount}
          bestRating={data.bestRating}
          worstRating={data.worstRating}
          isAuthorized={isAuthorized}
          link={data.link}
          image={data.thumbnail?.src || ''}
          labels={data.labels}
          title={data.name}
          series={data.series}
          description={data.nameEng}
          price={data.specialPrice ?? data.finalPrice ?? data.basePrice}
          oldPrice={data.finalPrice ? data.basePrice : null}
          rating={data.rating}
          bonuses={data.bonusPoints}
          bonusesMax={data.bonusPointsMax}
          bonusesLabel={data.labelsBonus[0]?.name}
          brand={{
            color: brand?.color,
            name: brand?.name,
            url: brand?.url
          }}
          isInWishlist={isInWishlist}
          isInCompare={isInCompare}
          isInCart={isInCart}
          options={[]}
          onClick={onClick}
          onCartClick={onCartClick}
          onWishlistClick={onWishlistClick}
          onCompareClick={onCompareClick}
          onOptionClick={onOptionClick}
          onSubscribeClick={onSubscribeClick}
          isSelected={isSelected}
          onSelect={onSelect}
          className={className}
          loadingCartButton={cartLoading}
          type={type}
          currency={data.currency}
          discount={data.discount}
        />
      )}
    </>
  );
}
